import React, { useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LaunchIcon from '@mui/icons-material/Launch';
import ReputationPill from '../common/ReputationPill';
import IdeaMetricsDisplay from './IdeaMetricsDisplay';
import IdeaMetricsChips from './IdeaMetricsChips';
import IdeaSimulationRunner from './IdeaSimulationRunner';
import RelatedSimulationsSection from './RelatedSimulationsSection';
import { IdeaData, IdeaDraft } from '../../hooks/useIdeaResults';
import { logger } from '../../utils/logger';

interface IdeaDraftCardProps {
  draft: IdeaDraft & { pulseData: any };
  index: number;
  ideaData: IdeaData;
  ideaId: string | undefined;
  expandedMetrics: { [key: string]: boolean };
  expandedDetails: { [key: string]: boolean };
  expandedSimulations: { [key: string]: boolean };
  onToggleMetrics: (draftId: string) => void;
  onToggleDetails: (draftId: string) => void;
  onToggleSimulations: (draftId: string) => void;
  onCopyDraft: (text: string, draftId: string) => Promise<void>;
  getImpactColor: (level: string) => string;
  getBaselineDeltaWord: (delta: string) => string;
  getInitialSimulationLimit: () => number;
}

const IdeaDraftCard: React.FC<IdeaDraftCardProps> = ({
  draft,
  index,
  ideaData,
  ideaId,
  expandedMetrics,
  expandedDetails,
  expandedSimulations,
  onToggleMetrics,
  onToggleDetails,
  onToggleSimulations,
  onCopyDraft,
  getImpactColor,
  getBaselineDeltaWord,
  getInitialSimulationLimit
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Parse brandPulseJson to get complete data
  let pulseData: any = {};
  if (draft.brandPulseJson) {
    try {
      pulseData = JSON.parse(draft.brandPulseJson);
    } catch (e) {
      logger.error('Error parsing brandPulseJson:', e);
    }
  }

  const impactColor = pulseData.reputation?.color || draft.reputation?.color || getImpactColor(draft.projectedImpactLevel || 'Unknown');
  // On mobile: Details open for first idea, closed for others. On desktop: always open
  const isDetailsExpanded = expandedDetails[draft.id] ?? (isMobile ? index === 0 : true);

  return (
    <Paper 
      key={draft.id} 
      id={`draft-${draft.id}`}
      elevation={2}
      data-first-card={index === 0 ? true : undefined}
      sx={{ 
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        bgcolor: (theme) => theme.palette.background.paper,
        borderLeft: `4px solid ${impactColor}`,
        overflow: 'hidden',
        position: 'relative',
        ...(isMobile && { scrollSnapAlign: 'start' }),
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 3, md: 4 } }}>
        {/* Metrics Chips with Run Full Simulation Button */}
        <IdeaMetricsChips
          pulseData={pulseData}
          draft={draft}
          isMobile={isMobile}
          isVerySmall={isMobile && window.innerWidth <= 360}
          impactColor={impactColor}
          title="Idea Content"
          simulationButton={
            <IdeaSimulationRunner
              draft={draft}
              ideaData={ideaData}
              ideaId={ideaId}
            />
          }
        />
        
        {/* Draft Text Content */}
        {isMobile ? (
          // Mobile: Simplified layout without inner box
          <Box sx={{ position: 'relative', mb: 2 }}>
            {/* Copy Icon - Floating action */}
            <IconButton
              onClick={() => onCopyDraft(draft.text, draft.id)}
              aria-label="Copy to clipboard"
              sx={{ 
                position: 'absolute',
                top: -8,
                right: -8,
                width: '32px',
                height: '32px',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#F8F9FA',
                color: (theme) => theme.palette.mode === 'dark' ? '#cbd5e1' : '#495057',
                border: 1,
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF',
                zIndex: 10,
                '&:hover': { 
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#E9ECEF' 
                }
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            
            <Typography variant="body1" sx={{ 
              whiteSpace: 'pre-wrap', 
              fontSize: '1rem',
              lineHeight: 1.5,
              fontWeight: 500,
              pr: 3,  // Space for copy button
              color: 'text.primary'
            }}>
              {draft.text}
            </Typography>
          </Box>
        ) : (
          // Desktop: Keep the original boxed design
          <Box sx={{ 
            p: 3, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#111827' : '#F8F9FA', 
            color: 'text.primary', 
            borderRadius: 2,
            border: 1,
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E9ECEF',
            mb: 3,
            position: 'relative'
          }}>
            {/* Copy Icon - Integrated into content card */}
            <Box sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8,
              zIndex: 10
            }}>
              <IconButton
                onClick={() => onCopyDraft(draft.text, draft.id)}
                aria-label="Copy to clipboard"
                sx={{ 
                  width: '28px',
                  height: '28px',
                  color: (theme) => theme.palette.mode === 'dark' ? '#cbd5e1' : '#495057',
                  opacity: 0.8,
                  '&:hover': { 
                    opacity: 1, 
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#E9ECEF' 
                  }
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Typography variant="body1" sx={{ 
              whiteSpace: 'pre-wrap', 
              fontSize: '1.1rem', 
              lineHeight: 1.4,
              fontWeight: 500,
              pr: 4  // Add padding to avoid overlap with copy button
            }}>
              {draft.text}
            </Typography>
          </Box>
        )}

        {/* Collapsible Details Section for Mobile */}
        {isMobile ? (
          <Box sx={{ mb: 3 }}>
            <Accordion 
              expanded={isDetailsExpanded}
              onChange={() => onToggleDetails(draft.id)}
              sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {/* Media Idea */}
                  {draft.imageIdea && (
                    <Box sx={{ 
                      p: { xs: 1.5, sm: 2 }, 
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.12)' : '#E5F4FC', 
                      color: (theme) => theme.palette.mode === 'dark' ? '#9bd1e3' : '#118AB2', 
                      borderRadius: 1, 
                      border: 1, 
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.35)' : '#BBDEFB'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '13px' }}>
                        Media Concept
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontStyle: 'italic', 
                        lineHeight: 1.4,
                        fontSize: '12px'
                      }}>
                        {draft.imageIdea}
                      </Typography>
                    </Box>
                  )}

                  {/* Strategy */}
                  {draft.rationale && (
                    <Box sx={{ 
                      p: { xs: 1.5, sm: 2 }, 
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.12)' : '#E5F4FC', 
                      color: (theme) => theme.palette.mode === 'dark' ? '#9bd1e3' : '#118AB2', 
                      borderRadius: 1, 
                      border: 1, 
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.35)' : '#BBDEFB'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, mb: 1, fontSize: '13px' }}>
                        Strategy
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        lineHeight: 1.4,
                        fontSize: '12px'
                      }}>
                        {draft.rationale}
                      </Typography>
                    </Box>
                  )}

                  {/* Additional Recommendations */}
                  {(pulseData.recommendation || draft.recommendation) && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 209, 102, 0.16)' : '#FFECB3', 
                      color: (theme) => theme.palette.mode === 'dark' ? '#FFD166' : '#725C2C', 
                      borderRadius: 1, 
                      border: 1, 
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 209, 102, 0.35)' : '#FFC75F'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, mb: 1, fontSize: '13px' }}>
                        Additional Recommendations
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        lineHeight: 1.4,
                        fontSize: '12px'
                      }}>
                        {pulseData.recommendation || draft.recommendation}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : (
          <>
            {/* Desktop Details - Always Visible */}
            {/* Media Idea */}
            {draft.imageIdea && (
              <Box sx={{ 
                mb: { xs: 2, sm: 3 }, 
                p: { xs: 2, sm: 3 }, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.12)' : '#E5F4FC', 
                color: (theme) => theme.palette.mode === 'dark' ? '#9bd1e3' : '#118AB2', 
                borderRadius: 1, 
                border: 1, 
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.35)' : '#BBDEFB'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Media Concept
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.4 }}>
                  {draft.imageIdea}
                </Typography>
              </Box>
            )}

            {/* Strategy */}
            {draft.rationale && (
              <Box sx={{ 
                mb: { xs: 2, sm: 3 }, 
                p: { xs: 2, sm: 3 }, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.12)' : '#E5F4FC', 
                color: (theme) => theme.palette.mode === 'dark' ? '#9bd1e3' : '#118AB2', 
                borderRadius: 1, 
                border: 1, 
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17,138,178,0.35)' : '#BBDEFB'
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                  Strategy
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  {draft.rationale}
                </Typography>
              </Box>
            )}

            {/* Additional Recommendations */}
            {(pulseData.recommendation || draft.recommendation) && (
              <Box sx={{ 
                mb: { xs: 2, sm: 3 }, 
                p: { xs: 2, sm: 3 }, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 209, 102, 0.16)' : '#FFECB3', 
                color: (theme) => theme.palette.mode === 'dark' ? '#FFD166' : '#725C2C', 
                borderRadius: 1, 
                border: 1, 
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 209, 102, 0.35)' : '#FFC75F'
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  Additional Recommendations
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                  {pulseData.recommendation || draft.recommendation}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Additional Analytics - moved after recommendations */}
        <IdeaMetricsDisplay
          draft={draft}
          index={index}
          expandedMetrics={expandedMetrics}
          onToggleMetrics={onToggleMetrics}
          getImpactColor={getImpactColor}
          getBaselineDeltaWord={getBaselineDeltaWord}
        />

        {/* Related Simulations - moved to end */}
        <RelatedSimulationsSection
          ideaData={ideaData}
          draftId={draft.id}
          expandedSimulations={expandedSimulations}
          onToggleSimulations={onToggleSimulations}
          getInitialSimulationLimit={getInitialSimulationLimit}
        />
      </Box>
    </Paper>
  );
};

export default IdeaDraftCard;
